import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  Label,
  Input,
  TextArea,
  PrimaryButton,
  TextButton,
  ButtonGroup,
  ErrorMessage,
} from '../../styles/CommonStyles';
import Modal from '../common/Modal';
import { createInvitation, listRoles } from '../../services/rbacService';
import { groupPermissionsByCategory } from './RoleDetailPanel';
import { useRbac } from '../../hooks/useRbac';
import { useDeveloperOrg } from '../../hooks/useDeveloperOrg';
import RoleEditorModal from './RoleEditorModal';

// ── Types ────────────────────────────────────────────────────────────────────

interface AccountRole {
  roleId: string;
  name: string;
  description?: string;
  status: 'active' | 'disabled';
  grants?: string[];
  effectivePermissions?: string[];
  system?: boolean;
  templateKey?: string | null;
  slug?: string;
  inheritsFrom?: string[];
  assignedMemberCount?: number;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSent: () => void;
  /** Prefill for the email field (in-chat invite: "invite jane@acme.com"
   *  opens this modal with the address already typed). Applied on open only —
   *  never overwrites something the inviter has since typed. */
  initialEmail?: string;
}

// ── Styled components ────────────────────────────────────────────────────────

const CompactFormGroup = styled.div`
  margin-bottom: 0.625rem;
`;

const CompactLabel = styled(Label)`
  font-size: 0.8125rem;
  margin-bottom: 0.25rem;
`;

const OptionalHint = styled.span`
  font-weight: 400;
  color: var(--color-text-tertiary);
`;

// Member type segmented control
const SegmentedControl = styled.div`
  display: flex;
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-md);
  overflow: hidden;
`;

const SegmentBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.45rem var(--spacing-sm);
  border: none;
  background: ${props => (props.$active ? 'var(--color-accent)' : 'var(--color-background-primary)')};
  color: ${props => (props.$active ? 'var(--color-background-primary)' : 'var(--color-text-secondary)')};
  font-family: var(--font-family-text);
  font-size: 0.875rem;
  font-weight: ${props => (props.$active ? '600' : '400')};
  cursor: pointer;
  transition: all var(--transition-fast);
  text-transform: lowercase;

  &:hover {
    background: ${props => (props.$active ? 'var(--color-accent-hover)' : 'var(--color-background-hover)')};
  }

  &:focus {
    outline: none;
  }
`;

// Role card grid — roomier cards, as many columns as the width honestly fits.
const RoleCardGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 0.625rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const RoleCard = styled.label<{ $selected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.625rem 1.75rem 0.625rem 0.75rem;
  border: 2px solid ${props => (props.$selected ? 'var(--color-border-focus)' : 'var(--color-border-light)')};
  border-radius: var(--radius-lg);
  cursor: pointer;
  background: ${props => (props.$selected ? 'var(--color-accent-subtle)' : 'var(--color-background-primary)')};
  transition: all var(--transition-fast);
  box-shadow: ${props => (props.$selected ? 'var(--shadow-sm)' : 'none')};

  &:hover {
    border-color: var(--color-border-focus);
    background: var(--color-accent-subtle);
  }

  input[type='checkbox'] {
    display: none;
  }
`;

const CardRoleName = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.78125rem;
  font-weight: 600;
  color: var(--color-text-primary);
  text-transform: lowercase;
`;

const CardRoleDesc = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.71875rem;
  color: var(--color-text-tertiary);
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardPermCount = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  color: var(--color-accent-text);
  margin-top: 0.2rem;
`;

/* ⓘ affordance on every role card (owner directive 2026-08-12): one click
   opens the full permission breakdown below the grid — hovering shows the
   role description. Intuitive without leaving the invite flow. */
const InfoBtn = styled.button<{ $active?: boolean }>`
  position: absolute;
  top: 0.4rem;
  right: 0.4rem;
  width: 17px;
  height: 17px;
  border-radius: 50%;
  border: 1px solid ${props => (props.$active ? 'var(--color-border-focus)' : 'transparent')};
  background: ${props => (props.$active ? 'var(--color-accent-subtle)' : 'transparent')};
  color: ${props => (props.$active ? 'var(--color-accent-text)' : 'var(--color-text-tertiary)')};
  font-size: 0.65rem;
  font-weight: 600;
  font-style: italic;
  font-family: Georgia, serif;
  line-height: 1;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all var(--transition-fast);

  &:hover {
    border-color: var(--color-border-focus);
    color: var(--color-accent-text);
    background: var(--color-accent-subtle);
  }
`;

const RecommendedTag = styled.span`
  align-self: flex-start;
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: lowercase;
  color: var(--color-accent-text);
  background: var(--color-accent-subtle);
  border: 1px solid var(--color-border-focus);
  border-radius: var(--radius-pill);
  padding: 0.05rem 0.4rem;
  margin-bottom: 0.15rem;
`;

const NewRoleCard = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.15rem;
  padding: 0.5rem;
  border: 2px dashed var(--color-border-medium);
  border-radius: var(--radius-lg);
  background: var(--color-background-primary);
  color: var(--color-text-tertiary);
  font-family: var(--font-family-text);
  font-size: 0.78125rem;
  font-weight: 600;
  cursor: pointer;
  min-height: 62px;
  transition: all var(--transition-fast);
  text-transform: lowercase;

  &:hover {
    border-color: var(--color-border-focus);
    color: var(--color-accent-text);
    background: var(--color-accent-subtle);
  }
`;

/* Permission breakdown — permanently visible under the role grid (owner
   directive 2026-08-13: the old 180px pop-in strip was too small to read).
   Shows the ⓘ-pinned role, else the role the inviter just picked, else the
   recommendation, so it's never empty. */
const RoleDetailPanel = styled.div`
  margin-top: 0.75rem;
  border: 1px solid var(--color-border-focus);
  background: var(--color-accent-subtle);
  border-radius: var(--radius-lg);
  padding: 0.875rem 1rem;
  flex: 0 0 auto;
  min-height: 170px;
  max-height: 300px;
  overflow-y: auto;

  @media (max-width: 860px) {
    max-height: 240px;
  }
`;

const RoleDetailHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
`;

const RoleDetailTitle = styled.span`
  font-family: var(--font-family-text);
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--color-text-primary);
  text-transform: lowercase;
`;

const RoleDetailClose = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: var(--color-text-tertiary);
  font-size: 0.9375rem;
  line-height: 1;
  padding: 0 0.125rem;

  &:hover { color: var(--color-text-primary); }
`;

const RoleDetailDesc = styled.div`
  font-family: var(--font-family-text);
  font-size: 0.75rem;
  color: var(--color-text-secondary);
  line-height: 1.4;
  margin-bottom: 0.375rem;
`;

const PermCategory = styled.div`
  font-family: var(--font-family-text);
  font-size: 0.625rem;
  font-weight: 700;
  color: var(--color-text-tertiary);
  text-transform: lowercase;
  letter-spacing: 0.06em;
  margin: 0.375rem 0 0.125rem;
`;

const PermChip = styled.span`
  display: inline-block;
  font-family: var(--font-family-text);
  font-size: 0.6875rem;
  color: var(--color-accent-text);
  background: var(--color-background-primary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-pill);
  padding: 0.05rem 0.45rem;
  margin: 0.1rem 0.2rem 0.1rem 0;
`;

const LoadingText = styled.div`
  font-family: var(--font-family-text);
  color: var(--color-text-tertiary);
  font-size: 0.875rem;
  padding: var(--spacing-sm) 0;
`;

// Collapsible message
const AddMessageLink = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-family: var(--font-family-text);
  font-size: 0.8125rem;
  color: var(--color-text-tertiary);
  padding: 0;
  text-align: left;
  transition: color var(--transition-fast);
  display: block;
  margin-bottom: 0.25rem;

  &:hover {
    color: var(--color-text-primary);
  }
`;

const SuccessBanner = styled.div`
  grid-column: 1 / -1;
  background: var(--color-background-secondary);
  border: 1px solid var(--color-success);
  border-radius: var(--radius-lg);
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--color-success);
  font-family: var(--font-family-text);
  font-weight: 500;
  font-size: 0.9375rem;
`;

// "Sent!" alone left admins believing the person was already on the team — and
// then wondering why the add-people picker couldn't see them. Say what happens next.
const SuccessDetail = styled.div`
  margin-top: 0.5rem;
  color: var(--color-text-secondary);
  font-weight: 400;
  font-size: 0.8125rem;
  line-height: 1.5;
`;

// Two-column body (owner directive 2026-08-13: the modal was cramped and the
// permission window too small — use the real estate). Identity fields live
// left; roles + a permanently visible permission panel live right, so "what
// will this person be able to do" never has to be summoned or scrolled for.
const BodyGrid = styled.div`
  padding: 1.5rem;
  display: grid;
  grid-template-columns: 320px minmax(0, 1fr);
  gap: 1.5rem;
  align-items: stretch;
  max-height: min(74vh, 760px);

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
    max-height: none;
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;

  @media (min-width: 861px) {
    border-right: 1px solid var(--color-border-light);
    padding-right: 1.5rem;
  }
`;

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;

  @media (min-width: 861px) {
    overflow: hidden;
  }
`;

const RoleScroll = styled.div`
  min-height: 0;
  display: flex;
  flex-direction: column;

  @media (min-width: 861px) {
    flex: 1;
    overflow-y: auto;
    padding-right: 2px;
  }
`;

// ── Component ────────────────────────────────────────────────────────────────

// Pick the recommended role for a member type from whatever roles the
// workspace actually has. External (client-portal) members are steered to the
// limited portal role; internal members to the standard staff role. Matching
// is by template key first, then id/slug, so custom workspaces still resolve.
function findRecommendedRoleId(roles: AccountRole[], memberType: 'internal' | 'client-portal'): string | null {
  const byKey = (key: string) =>
    roles.find(r => r.templateKey === key || r.roleId === key || r.slug === key);
  const candidates =
    memberType === 'client-portal'
      ? [byKey('client-portal'), byKey('read-only')]
      : [byKey('associate'), byKey('member'), byKey('paralegal')];
  return candidates.find(Boolean)?.roleId ?? null;
}

const InviteModal: React.FC<Props> = ({ isOpen, onClose, onSent, initialEmail }) => {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [memberType, setMemberType] = useState<'internal' | 'client-portal'>('internal');
  const [message, setMessage] = useState('');
  const [showMessage, setShowMessage] = useState(false);
  const [roles, setRoles] = useState<AccountRole[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set());
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  // Invite link (task #42/#44): always offered after a successful create;
  // linkFallbackUrl holds it, emailWasSent drives the banner copy.
  const [linkFallbackUrl, setLinkFallbackUrl] = useState<string | null>(null);
  const [emailWasSent, setEmailWasSent] = useState(true);
  const [linkCopied, setLinkCopied] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // ⓘ details target + inline quick-create (owner directive 2026-08-12).
  const [detailRoleId, setDetailRoleId] = useState<string | null>(null);
  const [showRoleEditor, setShowRoleEditor] = useState(false);
  // Once the inviter touches the role selection it is THEIRS — switching
  // member type stops swapping the recommendation underneath them.
  const [rolesTouched, setRolesTouched] = useState(false);
  const { hasPermission } = useRbac();
  const { isDeveloperOrg } = useDeveloperOrg();
  const canCreateRoles = hasPermission('roles.create');

  const loadRoles = React.useCallback(() => {
    setLoadingRoles(true);
    return listRoles()
      .then(data => {
        const active = (data.roles as AccountRole[]).filter(r => r.status === 'active');
        setRoles(active);
        return active;
      })
      .catch(() => [] as AccountRole[])
      .finally(() => setLoadingRoles(false));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadRoles();
  }, [isOpen, loadRoles]);

  // Email prefill on open (reset() clears the field on close, so an empty
  // field here means the inviter hasn't typed — safe to fill).
  useEffect(() => {
    if (!isOpen || !initialEmail) return;
    setEmail(prev => (prev ? prev : initialEmail));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, initialEmail]);

  const recommendedRoleId = useMemo(
    () => findRecommendedRoleId(roles, memberType),
    [roles, memberType]
  );

  // Role selection is REQUIRED (owner directive 2026-08-12) — and the
  // recommended role is pre-selected so the common case stays one click:
  // type an email, send. Runs until the inviter takes over the selection.
  useEffect(() => {
    if (!isOpen || rolesTouched || !recommendedRoleId) return;
    setSelectedRoles(new Set([recommendedRoleId]));
  }, [isOpen, rolesTouched, recommendedRoleId]);

  // Recommended-first ordering; the rest keep the server's order.
  const orderedRoles = useMemo(() => {
    if (!recommendedRoleId) return roles;
    const rec = roles.find(r => r.roleId === recommendedRoleId);
    return rec ? [rec, ...roles.filter(r => r.roleId !== recommendedRoleId)] : roles;
  }, [roles, recommendedRoleId]);

  // The panel always has a subject: the ⓘ-pinned role wins, else the most
  // recent selection, else the recommendation — never an empty box.
  const pinnedDetailRole = detailRoleId ? roles.find(r => r.roleId === detailRoleId) ?? null : null;
  const detailRole =
    pinnedDetailRole ??
    (selectedRoles.size > 0
      ? roles.find(r => selectedRoles.has(r.roleId)) ?? null
      : roles.find(r => r.roleId === recommendedRoleId) ?? null);

  // Group the detail role's permissions by catalog category — the SHARED
  // grouping (RoleDetailPanel.tsx, 2026-08-19) so this modal and the
  // onboarding invite step can never drift on how a role is explained.
  const detailGroups = useMemo(
    () => (detailRole
      ? groupPermissionsByCategory(detailRole.effectivePermissions ?? detailRole.grants ?? [])
      : []),
    [detailRole],
  );

  const reset = () => {
    setEmail('');
    setDisplayName('');
    setMemberType('internal');
    setMessage('');
    setShowMessage(false);
    setSelectedRoles(new Set());
    setErrors({});
    setSuccess(false);
    setLinkFallbackUrl(null);
    setEmailWasSent(true);
    setLinkCopied(false);
    setDetailRoleId(null);
    setShowRoleEditor(false);
    setRolesTouched(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  // One-click "invite another" (owner directive 2026-08-13): clear only the
  // person-specific fields and return to the form. Member type and the role
  // selection survive on purpose — batch invites are usually same-role, and
  // an untouched selection re-arms the recommended-role preselect anyway.
  const handleInviteAnother = () => {
    setEmail('');
    setDisplayName('');
    setMessage('');
    setShowMessage(false);
    setErrors({});
    setSuccess(false);
    setLinkFallbackUrl(null);
    setEmailWasSent(true);
    setLinkCopied(false);
  };

  const toggleRole = (roleId: string) => {
    setRolesTouched(true);
    setSelectedRoles(prev => {
      const next = new Set(prev);
      if (next.has(roleId)) next.delete(roleId); else next.add(roleId);
      return next;
    });
    setErrors(prev => ({ ...prev, roles: '' }));
  };

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) errs.email = 'email is required';
    else if (!emailRegex.test(email.trim())) errs.email = 'enter a valid email address';
    // Required only when there is something to pick — a workspace with no
    // seeded roles must still be able to invite (the server assigns its
    // standard default), never dead-end on an unsatisfiable requirement.
    if (roles.length > 0 && selectedRoles.size === 0) {
      errs.roles = 'select at least one role — it defines what they can see and do';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSend = async () => {
    if (!validate()) return;
    setSaving(true);
    setErrors({});
    try {
      const res = await createInvitation({
        email: email.trim().toLowerCase(),
        displayName: displayName.trim() || undefined,
        roleIds: Array.from(selectedRoles),
        memberType,
        message: message.trim() || undefined,
      });
      // Truthful outcome (task #42) + always-copyable link (task #44): the
      // invite link is offered whether or not the email was delivered; the
      // banner copy says which happened. No auto-close — the owner decides
      // when they're done (they may want to copy the link either way).
      setEmailWasSent(res.emailSent !== false);
      setLinkFallbackUrl(res.acceptUrl ?? null);
      setSuccess(true);
      onSent();
    } catch (err: any) {
      setErrors({ submit: err?.message ?? 'invitation failed to send' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Invite Member"
      maxWidth="min(1060px, 94vw)"
      noScroll
      footer={
        success ? (
          <ButtonGroup>
            <TextButton onClick={handleClose}>close</TextButton>
            <PrimaryButton onClick={handleInviteAnother}>invite another member</PrimaryButton>
          </ButtonGroup>
        ) : (
          <ButtonGroup>
            <TextButton onClick={handleClose} disabled={saving}>Cancel</TextButton>
            {/* Also disabled while roles load: in that window roles=[] and
                the required-role validation is legitimately relaxed, so a
                fast click could send a role-less invite from a workspace
                that HAS roles (review 2026-08-13). */}
            <PrimaryButton onClick={handleSend} disabled={saving || loadingRoles}>
              {saving ? 'Sending…' : 'Send Invitation'}
            </PrimaryButton>
          </ButtonGroup>
        )
      }
    >
      <BodyGrid>
      {success ? (
        <SuccessBanner>
          {emailWasSent
            ? `invitation sent to ${email.trim().toLowerCase()}`
            : `invitation created for ${email.trim().toLowerCase()}`}
          <SuccessDetail>
            {linkFallbackUrl ? (
              <>
                {emailWasSent
                  ? 'the invite email is on its way — you can also copy their invite link and send it yourself. '
                  : "the invite email couldn't be delivered — copy their invite link and send it to them directly. "}
                <button
                  type="button"
                  onClick={() => {
                    try { navigator.clipboard.writeText(linkFallbackUrl); } catch { /* noop */ }
                    setLinkCopied(true);
                    setTimeout(() => setLinkCopied(false), 2500);
                  }}
                  style={{
                    border: '1px solid var(--brand-gold)', background: '#fff', color: '#7a5f1f',
                    borderRadius: 999, padding: '0.2rem 0.7rem', fontSize: '0.78rem',
                    fontWeight: 600, cursor: 'pointer', textTransform: 'lowercase',
                  }}
                >
                  {linkCopied ? 'copied ✓' : 'copy invite link'}
                </button>
              </>
            ) : (
              <>
                they’ll show as <strong>invited</strong> in your members list until they accept. once
                they do, they become a full member and you can add them to threads.
              </>
            )}
          </SuccessDetail>
        </SuccessBanner>
      ) : (
        <>
        <LeftCol>
          <CompactFormGroup>
            <CompactLabel>Member Type</CompactLabel>
            <SegmentedControl>
              <SegmentBtn
                $active={memberType === 'internal'}
                onClick={() => setMemberType('internal')}
              >
                internal
              </SegmentBtn>
              <SegmentBtn
                $active={memberType === 'client-portal'}
                onClick={() => setMemberType('client-portal')}
              >
                external
              </SegmentBtn>
            </SegmentedControl>
          </CompactFormGroup>

          <CompactFormGroup>
            <CompactLabel>Email *</CompactLabel>
            <Input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: '' })); }}
              placeholder="colleague@example.com"
            />
            {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
          </CompactFormGroup>

          <CompactFormGroup>
            <CompactLabel>
              Display Name <OptionalHint>(optional)</OptionalHint>
            </CompactLabel>
            <Input
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              placeholder="Jane Smith"
            />
          </CompactFormGroup>

          {!showMessage ? (
            <AddMessageLink onClick={() => setShowMessage(true)}>
              + add personal message
            </AddMessageLink>
          ) : (
            <CompactFormGroup>
              <CompactLabel>
                Personal Message <OptionalHint>(optional)</OptionalHint>
              </CompactLabel>
              <TextArea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="add a note to include in the invitation email…"
                rows={3}
              />
            </CompactFormGroup>
          )}

          {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}
        </LeftCol>

        <RightCol>
          <CompactFormGroup style={{ display: 'flex', flexDirection: 'column', minHeight: 0, marginBottom: 0, flex: 1 }}>
            <CompactLabel>
              Roles <OptionalHint>(required — the panel below shows exactly what each role can do)</OptionalHint>
            </CompactLabel>
            {loadingRoles && <LoadingText>loading…</LoadingText>}
            {!loadingRoles && (
              <RoleScroll>
              <RoleCardGrid>
                {orderedRoles.map(role => {
                  const permCount = role.effectivePermissions?.length ?? role.grants?.length;
                  const isRecommended = role.roleId === recommendedRoleId;
                  return (
                    <RoleCard
                      key={role.roleId}
                      $selected={selectedRoles.has(role.roleId)}
                      title={role.description}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoles.has(role.roleId)}
                        onChange={() => toggleRole(role.roleId)}
                      />
                      <InfoBtn
                        type="button"
                        $active={detailRoleId === role.roleId}
                        aria-label={`view ${role.name} permissions`}
                        title={`view ${role.name} permissions`}
                        onClick={e => {
                          // A click on ⓘ inspects — it must never toggle the
                          // card's checkbox underneath (the card is a <label>).
                          e.preventDefault();
                          e.stopPropagation();
                          setDetailRoleId(prev => (prev === role.roleId ? null : role.roleId));
                        }}
                      >
                        i
                      </InfoBtn>
                      {isRecommended && (
                        <RecommendedTag>
                          recommended for {memberType === 'client-portal' ? 'external' : 'internal'}
                        </RecommendedTag>
                      )}
                      <CardRoleName>{role.name}</CardRoleName>
                      {role.description && (
                        <CardRoleDesc>{role.description}</CardRoleDesc>
                      )}
                      {permCount !== undefined && (
                        <CardPermCount>{permCount} permission{permCount !== 1 ? 's' : ''}</CardPermCount>
                      )}
                    </RoleCard>
                  );
                })}
                {canCreateRoles && (
                  <NewRoleCard
                    type="button"
                    onClick={() => setShowRoleEditor(true)}
                    title="create a role without leaving this invite"
                  >
                    <span>+ new role</span>
                    <span style={{ fontWeight: 400, fontSize: '0.6875rem' }}>
                      don't see the right fit?
                    </span>
                  </NewRoleCard>
                )}
              </RoleCardGrid>
              </RoleScroll>
            )}
            {errors.roles && <ErrorMessage>{errors.roles}</ErrorMessage>}
            {detailRole && (
              <RoleDetailPanel>
                <RoleDetailHeader>
                  <RoleDetailTitle>
                    {detailRole.name} — {(detailRole.effectivePermissions ?? detailRole.grants ?? []).length} permission{(detailRole.effectivePermissions ?? detailRole.grants ?? []).length !== 1 ? 's' : ''}
                  </RoleDetailTitle>
                  {pinnedDetailRole ? (
                    <RoleDetailClose
                      type="button"
                      aria-label="unpin role details"
                      onClick={() => setDetailRoleId(null)}
                    >
                      ×
                    </RoleDetailClose>
                  ) : (
                    <span style={{ fontSize: '0.6875rem', color: 'var(--color-text-tertiary)', whiteSpace: 'nowrap' }}>
                      ⓘ on any card pins its details here
                    </span>
                  )}
                </RoleDetailHeader>
                {detailRole.description && (
                  <RoleDetailDesc>{detailRole.description}</RoleDetailDesc>
                )}
                {detailGroups.length === 0 && (
                  <RoleDetailDesc>this role has no permissions yet.</RoleDetailDesc>
                )}
                {detailGroups.map(group => (
                  <div key={group.label}>
                    <PermCategory>{group.label}</PermCategory>
                    <div>
                      {group.items.map(item => (
                        <PermChip key={item}>{item}</PermChip>
                      ))}
                    </div>
                  </div>
                ))}
              </RoleDetailPanel>
            )}
          </CompactFormGroup>
        </RightCol>
        </>
      )}
      </BodyGrid>

      {/* Inline quick-create (owner directive 2026-08-12): the full role
          editor layered OVER the invite, so the inviter never loses the
          email/name they already typed. The new role is selected on save. */}
      {showRoleEditor && (
        <RoleEditorModal
          role={null}
          allRoles={roles as any}
          developerOrg={isDeveloperOrg}
          onClose={() => setShowRoleEditor(false)}
          onSaved={(newRoleId?: string) => {
            setShowRoleEditor(false);
            loadRoles().then(active => {
              if (newRoleId && active.some(r => r.roleId === newRoleId)) {
                setRolesTouched(true);
                setSelectedRoles(prev => new Set(prev).add(newRoleId));
                setDetailRoleId(newRoleId);
              }
            });
          }}
        />
      )}
    </Modal>
  );
};

export default InviteModal;
